import { create } from 'zustand';
import type { AnnouncementModalType, ViewStore } from '@/types';
import { toast } from '@/utils/client/toast';

export const useViewStore = create<ViewStore>((set) => ({
  showMenuDrawer: false,
  showChapterNote: false,
  showFlashcardAnswer: false,
  showAnnouncementsModal: false,
  announcementModalType: 'user',
  hasShownAnnouncements: false,
  showExplanations: [],
  dialog: undefined,
  timer: {
    time: 0,
    isRunning: false,
    closedAt: undefined,
    setTime: (timeOrUpdater) =>
      set((state) => {
        const newTime =
          typeof timeOrUpdater === 'function' ? timeOrUpdater(state.timer.time) : timeOrUpdater;
        if (newTime === state.timer.time) return state;
        return {
          timer: { ...state.timer, time: newTime },
        };
      }),
    setRunning: (isRunning) =>
      set((state) => {
        if (state.timer.isRunning === isRunning) return state;
        return { timer: { ...state.timer, isRunning } };
      }),
    setClosedAt: (closedAt) =>
      set((state) => {
        if (state.timer.closedAt === closedAt) return state;
        return { timer: { ...state.timer, closedAt } };
      }),
  },
  toggleDialog: (params) =>
    set((state) => {
      return {
        dialog: state.dialog ? undefined : params,
      };
    }),
  toggleMenuDrawer: () =>
    set((state) => {
      const isOpening = !state.showMenuDrawer;

      if (!isOpening) {
        // Drawer is being closed: store timestamp
        if (state.timer.isRunning) toast.info('Timer läuft weiter...');
        return {
          showMenuDrawer: isOpening,
          timer: {
            ...state.timer,
            closedAt: new Date(),
          },
        };
      }

      // Drawer is opening: keep timestamp for now, logic will handle reset
      return { showMenuDrawer: isOpening };
    }),
  toggleChapterNote: () => set((state) => ({ showChapterNote: !state.showChapterNote })),
  toggleFlashcardAnswer: (newState) =>
    set((state) => ({
      showFlashcardAnswer: typeof newState !== 'undefined' ? newState : !state.showFlashcardAnswer,
    })),
  toggleAnnouncementsModal: (type: AnnouncementModalType = 'user') =>
    set((state) => ({
      showAnnouncementsModal: !state.showAnnouncementsModal,
      announcementModalType: type,
    })),
  setHasShownAnnouncements: (value: boolean) => set(() => ({ hasShownAnnouncements: value })),
  toggleShowExplanations: (id) =>
    set((state) => {
      const exists = state.showExplanations.includes(id);
      return {
        showExplanations: exists
          ? state.showExplanations.filter((val) => val !== id)
          : [...state.showExplanations, id],
      };
    }),
  emptyShowExplanations: () => set(() => ({ showExplanations: [] })),
}));
